import React, { useState } from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Divider, Stack, Paper, Container } from "@mui/material";
import Link from "@mui/material/Link";
import { useNavigate } from "react-router-dom";

import ContinueWithGoogle from "../Login/components/ContinueWithGoogle";
import SignupForm from "./RegisterForm";

export default function RegisterView() {
  const [isSuccessfullySignedUp, setSsSuccessfullySignedUp] =
    useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleGoogleSignIn = () => {
    const serverUrl = process.env.REACT_APP_API_URL;
    window.location.href = `${serverUrl}/auth/signin/google`;
  };

  const handleSignIn = () => {
    navigate("/login");
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "100vh",
          padding: "40px 0",
        }}
      >
        {/* Success Message */}
        {isSuccessfullySignedUp && (
          <Paper
            elevation={3}
            sx={{
              padding: { xs: 3, sm: 4 },
              borderRadius: 3,
              width: "100%",
              maxWidth: "400px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
              textAlign: "center",
            }}
          >
            {/* Logo instead of Avatar for success */}
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <img
                src="/logo.png"
                alt="Podcast Assistant Logo"
                style={{
                  height: "120px",
                  width: "auto",
                }}
              />
            </Box>
            <Typography
              component="h1"
              variant="h5"
              sx={{ fontWeight: 600, color: "text.primary", mb: 2 }}
            >
              Sign Up Successful!
            </Typography>
            <Typography
              component="p"
              sx={{ marginY: 2, textAlign: "center", color: "text.secondary" }}
            >
              You have successfully signed up. We have sent you an email to{" "}
              <Typography
                component="span"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                {userEmail}
              </Typography>
              . Please verify your email address.
            </Typography>
            <Typography component="p" sx={{ marginY: 2, textAlign: "center" }}>
              Back to{" "}
              <Link
                href="/login"
                variant="body2"
                sx={{
                  color: "primary.main",
                  textDecoration: "none",
                  fontWeight: 500,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                Sign In
              </Link>
            </Typography>
          </Paper>
        )}

        {/* Registration Form */}
        {!isSuccessfullySignedUp && (
          <Paper
            elevation={3}
            sx={{
              padding: { xs: 3, sm: 4 },
              borderRadius: 3,
              width: "100%",
              maxWidth: "400px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            }}
          >
            {/* Sign Up Header */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 3,
              }}
            >
              {/* Logo instead of Avatar for register form */}
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <img
                  src="/logo.png"
                  alt="Podcast Assistant Logo"
                  style={{
                    height: "80px",
                    width: "auto",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
              <Typography
                component="h1"
                variant="h5"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Sign Up
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, textAlign: "center" }}
              >
                Create your account to get started
              </Typography>
            </Box>

            {/* Social Login */}
            <Box sx={{ mb: 3 }}>
              <ContinueWithGoogle onClick={handleGoogleSignIn} />
            </Box>

            {/* Divider */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ mb: 3 }}
            >
              <Divider sx={{ flexGrow: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                or continue with email
              </Typography>
              <Divider sx={{ flexGrow: 1 }} />
            </Stack>

            {/* Signup Form */}
            <SignupForm
              handleSuccessfulSignup={(email: string) => {
                setSsSuccessfullySignedUp(true);
                setUserEmail(email);
              }}
            />
          </Paper>
        )}

        {/* Footer Links */}
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Already have an account?{" "}
            <Typography
              component="span"
              variant="body2"
              sx={{
                color: "primary.main",
                cursor: "pointer",
                fontWeight: 500,
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
              onClick={handleSignIn}
            >
              Sign in
            </Typography>
          </Typography>
        </Box>
      </Box>
    </Container>
  );
}
